import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

import { useAppState } from './AppState.js';
import { ErrorPanel, LoadingPanel, LoginPrompt, PageIntro } from './components.js';
import { mapError } from './errors.js';
import { resolveProfileDraft } from './profileDraft.js';
import { formatDate } from '../lib/formatting.js';

export function HomePage() {
  const { session, player, client, refreshIdentity, setBanner } = useAppState();
  const [email, setEmail] = useState(resolveProfileDraft(player, session).email);
  const [name, setName] = useState(resolveProfileDraft(player, session).name);

  const linkMutation = useMutation({
    mutationFn: async () => {
      if (!client) {
        throw new Error('API client unavailable');
      }
      return client.linkPlayer({
        email: email.trim(),
        ...(name.trim() ? { name: name.trim() } : {}),
      });
    },
    onSuccess: async () => {
      setBanner('success', 'Player profile linked successfully.');
      await refreshIdentity(true);
    },
    onError: (error: unknown) => setBanner('error', mapError(error)),
  });

  useEffect(() => {
    const draft = resolveProfileDraft(player, session);
    setEmail(draft.email);
    setName(draft.name);
  }, [player, session]);

  if (!session) {
    return <LoginPrompt message="Sign in to access your leagues and management tools." />;
  }

  if (player) {
    return <Navigate replace to="/leagues" />;
  }

  return (
    <>
      <PageIntro
        title="Link Player Profile"
        actions={<Link className="button-link" to="/leagues/new">Create League</Link>}
      />
      <section className="panel stack">
        <div className="empty-state">
          <div className="empty-state-title">Before you continue</div>
          <p className="muted">
            If a commissioner already created your player record, use the same email address they have on file so the app can link you automatically.
          </p>
          <p className="muted">
            If you expected league access already, an invite link or commissioner follow-up may still be needed after this step.
          </p>
          <p className="muted">Want to start your own league? Choose Create League above. Your player profile will be linked as part of setup.</p>
        </div>
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            void linkMutation.mutateAsync();
          }}
        >
          <div className="field">
            <label htmlFor="link-email">Email</label>
            <input
              id="link-email"
              name="email"
              type="email"
              placeholder="you@example.com"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="link-name">Display Name</label>
            <input
              id="link-name"
              name="name"
              type="text"
              placeholder="Your Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div className="inline-actions">
            <button type="submit" disabled={linkMutation.isPending}>
              {linkMutation.isPending ? 'Linking...' : 'Link Account'}
            </button>
            <Link className="nav-link" to="/">
              Refresh Setup
            </Link>
          </div>
        </form>
      </section>
    </>
  );
}

export function JoinInvitePage() {
  const { token = '' } = useParams();
  const { session, player, client, refreshIdentity, setActiveLeague, setBanner } = useAppState();
  const navigate = useNavigate();
  const [email, setEmail] = useState(resolveProfileDraft(player, session).email);
  const [name, setName] = useState(resolveProfileDraft(player, session).name);

  const inviteQuery = useQuery({
    queryKey: ['invite', token],
    enabled: Boolean(session && client && token),
    queryFn: async () => client!.getInviteByToken(token),
  });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      if (!client) {
        throw new Error('API client unavailable');
      }
      const nextEmail = email.trim();
      const nextName = name.trim();
      return client.acceptInvite(token, {
        ...(nextEmail ? { email: nextEmail } : {}),
        ...(nextName ? { name: nextName } : {}),
      });
    },
    onSuccess: async (accepted) => {
      await refreshIdentity(true);
      setBanner('success', 'Invite accepted.');
      setActiveLeague(accepted.leagueId);
      navigate('/leagues');
    },
    onError: (error: unknown) => setBanner('error', mapError(error)),
  });

  useEffect(() => {
    const draft = resolveProfileDraft(player, session);
    setEmail(draft.email);
    setName(draft.name);
  }, [player, session]);

  if (!session) {
    return <LoginPrompt message="Login is required before accepting an invite link." />;
  }

  if (inviteQuery.isLoading) {
    return <LoadingPanel title="Invite Link" message="Loading invite details." />;
  }

  if (inviteQuery.isError || !inviteQuery.data) {
    return <ErrorPanel title="Invite Link" message={mapError(inviteQuery.error)} />;
  }

  const inviteInfo = inviteQuery.data;
  const leagueName = inviteInfo.league?.name || inviteInfo.invite.league_id || 'Unknown League';
  const inviteScope = inviteInfo.invite.scope === 'season' ? 'season' : 'league';
  const seasonName = inviteInfo.season?.name || inviteInfo.invite.season_id || 'Selected Season';
  const linkedPlayerName = inviteInfo.player?.name || inviteInfo.invite.player_id || '';

  return (
    <>
      <PageIntro title="Join League" />
      <section className="panel stack">
        <p>
          <strong>League:</strong> {leagueName}
        </p>
        <p>
          <strong>Scope:</strong> {inviteScope === 'season' ? 'Season + League Access' : 'League Access'}
        </p>
        {inviteScope === 'season' ? (
          <p>
            <strong>Season:</strong> {seasonName}
          </p>
        ) : null}
        {linkedPlayerName ? (
          <p>
            <strong>Linking:</strong> This invite will link your login to {linkedPlayerName}.
          </p>
        ) : null}
        <p>
          <strong>Token:</strong> <code>{token}</code>
        </p>
        <p>
          <strong>Expires:</strong> {formatDate(inviteInfo.invite.expires_at)}
        </p>
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            void acceptMutation.mutateAsync();
          }}
        >
          <div className="row">
            <div className="field">
              <label>Email</label>
              <input
                type="email"
                value={email}
                placeholder="you@example.com"
                required
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
            <div className="field">
              <label>Name</label>
              <input
                type="text"
                value={name}
                placeholder="Your Name"
                onChange={(event) => setName(event.target.value)}
              />
            </div>
          </div>
          <p className="muted">
            We prefilled this from your login when available. Keeping the same email helps us match any existing player record the commissioner already created for you.
          </p>
          <div className="inline-actions">
            <button type="submit" disabled={acceptMutation.isPending}>
              {acceptMutation.isPending ? 'Accepting...' : 'Accept Invite'}
            </button>
            <Link className="nav-link" to="/leagues">
              Go to League List
            </Link>
          </div>
        </form>
      </section>
    </>
  );
}
